import { Box, ListItemIcon, ListItemText, TextField, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ContextMenuIconButton } from "Common/Components";
import { StyledSideBarButton } from "./StyledSideBarButton";

interface IChatLabel {
  name: string;
  active: boolean;
  isVisible?: boolean;
  isDisabled?: boolean;
  id?: string;
  hasBorder: boolean;
  isEnabledEdit: boolean;
  handleContextMenuButtonClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  renameTitle: (conversationId: string, title: string) => void;
  setEditChatTitleId: (id: string | undefined) => void;
  handleNavigate: (id: string | undefined) => void;
  isConversationLoading: boolean;
  handleSnackbar: (message: string, isSuccess: boolean, isWarning?: boolean) => void;
}

export const ChatLabel = (props: IChatLabel) => {
  const {
    name,
    active,
    isDisabled,
    id,
    hasBorder,
    isEnabledEdit: enableEdit,
    handleContextMenuButtonClick,
    renameTitle,
    setEditChatTitleId,
    isVisible,
    handleNavigate,
    isConversationLoading,
    handleSnackbar,
  } = props;
  const [hover, setHover] = useState(false);
  const [inputValue, setInputValue] = useState(name);
  const [isEnabledEdit, setIsEnabledEdit] = useState<boolean>(enableEdit);
  const [error, setError] = useState("");
  const textFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setIsEnabledEdit(enableEdit);
    if (enableEdit) {
      setInputValue(name);
      setError("");
    }
  }, [enableEdit]);

  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEnabledEdit]);

  useEffect(() => {
    if (!isEnabledEdit) {
      setInputValue(name);
    }
  }, [name, isEnabledEdit]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    if (e.target.value.trim().length === 0) {
      setError("Conversation title cannot be empty.");
    } else if (e.target.value.length < 25) {
      setError("Character limit should be between 25 to 200.");
    } else {
      setError("");
    }
  };

  const attemptRename = () => {
    const title = inputValue.trim();

    if (!title) {
      setError("Conversation title cannot be empty.");
      requestAnimationFrame(() => textFieldRef.current?.focus());
      return;
    }

    if (title.length < 25 || title.length > 200) {
      setError("Character limit should be between 25 to 200.");
      requestAnimationFrame(() => textFieldRef.current?.focus());
      return;
    }

    setError("");
    renameTitle(id ?? "", title);
  };

  const handleTap = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isConversationLoading && !isEnabledEdit && !isDisabled) {
      handleNavigate(id);
    } else {
      handleSnackbar(`Conversation loading, try again shortly.`, false, true);
    }
  };

  return (
    <Box>
      {isEnabledEdit ? (
        <TextField
          inputRef={textFieldRef}
          value={inputValue}
          slotProps={{
            htmlInput: { maxLength: 200, minLength: 25 },
            formHelperText: {
              sx: {
                paddingLeft: 2,
              },
            },
          }}
          onChange={(e) => handleInput(e)}
          onBlur={attemptRename}
          error={!!error}
          helperText={error || undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              attemptRename();
            }
            if (e.key === "Escape") {
              setIsEnabledEdit(false);
              setEditChatTitleId(undefined);
            }
          }}
          sx={{
            background: "#fff",
            fontWeight: "medium",
            outline: "none",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
            },
            "& .MuiOutlinedInput-input": { outline: "none" },
            "& .MuiInputBase-input": { outline: "none" },
          }}
        />
      ) : (
        <Tooltip title={name} arrow placement="right">
          <StyledSideBarButton
            selected={isVisible || active}
            disabled={isDisabled}
            hasborder={hasBorder.toString()}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            role="button"
            tabIndex={0}
          >
            <ListItemText
              onClick={handleTap}
              onTouchStart={handleTap}
              primary={name}
              slotProps={{
                primary: {
                  sx: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                },
              }}
            />

            <ListItemIcon>
              <ContextMenuIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleContextMenuButtonClick?.(e);
                }}
                active={active}
                isVisible={isVisible || hover}
              />
            </ListItemIcon>
          </StyledSideBarButton>
        </Tooltip>
      )}
    </Box>
  );
};
